/**
 * TournamentPage — màn hình chính cho game Kéo Búa Bao.
 * Route: /rounds/:roundId/tournament
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Volume2, VolumeX, Trophy, Swords } from 'lucide-react';
import { useGameSocket } from '@/lib/socket/useGameSocket';
import { useRpsSocket, useRpsChoose, useSpotlightMatch } from '@/lib/socket/useRpsSocket';
import { gameStore } from '@/store/gameStore';
import { rpsStore, type RPSMatchState } from '@/store/rpsStore';
import { Avatar } from '@/design-system/components/Avatar';
import { useAudio } from '@/lib/audio/useAudio';
import { ChatPanel } from '@/features/chat/ChatPanel';
import { BracketTree } from '@/features/tournament/BracketTree';
import styles from './TournamentPage.module.css';

// ---- Hooks ----

function useTimerMs(startedAt: number | null, timerSec: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(id);
  }, [startedAt]);
  if (!startedAt) return null;
  return Math.max(0, timerSec * 1000 - (now - startedAt));
}

// ---- Confetti ----

const CONFETTI_COLORS = ['#f43f5e', '#f97316', '#facc15', '#4ade80', '#38bdf8', '#a78bfa', '#fb7185', '#34d399'];

function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    type Particle = {
      x: number; y: number; vx: number; vy: number;
      color: string; w: number; h: number;
      rotation: number; rotationSpeed: number; opacity: number;
    };

    const particles: Particle[] = Array.from({ length: 160 }, () => ({
      x: Math.random() * canvas.width,
      y: -20 - Math.random() * 200,
      vx: (Math.random() - 0.5) * 4,
      vy: 2 + Math.random() * 4,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      w: 8 + Math.random() * 8,
      h: 4 + Math.random() * 4,
      rotation: Math.random() * Math.PI * 2,
      rotationSpeed: (Math.random() - 0.5) * 0.15,
      opacity: 0.85 + Math.random() * 0.15,
    }));

    let animId: number;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.07; // gravity
        p.vx *= 0.99;
        p.rotation += p.rotationSpeed;
        if (p.y < canvas.height + 40) alive = true;

        ctx.save();
        ctx.globalAlpha = p.opacity;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) animId = requestAnimationFrame(tick);
    };
    animId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 9999 }}
    />
  );
}

// ---- Components ----

const CHOICE_LABELS: Record<string, string> = {
  rock: '✊ Búa',
  paper: '✋ Bao',
  scissors: '✌️ Kéo',
};

const CHOICE_EMOJI: Record<string, string> = {
  rock: '✊',
  paper: '✋',
  scissors: '✌️',
};

function ChoiceButton({
  choice,
  onClick,
  disabled,
  selected,
}: {
  choice: 'rock' | 'paper' | 'scissors';
  onClick: () => void;
  disabled: boolean;
  selected: boolean;
}) {
  return (
    <button
      className={`${styles.choiceBtn} ${selected ? styles.choiceBtnSelected : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className={styles.choiceEmoji}>{CHOICE_EMOJI[choice]}</span>
      <span className={styles.choiceLabel}>{CHOICE_LABELS[choice]}</span>
    </button>
  );
}

/** Mô tả kết quả battle giữa 2 lựa chọn */
function getBattleEffect(winner: string, loser: string): string {
  if (winner === 'scissors' && loser === 'paper') return '✌️ cắt ✋';
  if (winner === 'paper' && loser === 'rock') return '✋ bọc ✊';
  if (winner === 'rock' && loser === 'scissors') return '✊ đập ✌️';
  return '';
}

function RevealDisplay({
  match,
}: {
  match: RPSMatchState;
  myParticipantId: string | null;
}) {
  const reveal = match.last_reveal;
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, [reveal?.result, reveal?.player1_choice, reveal?.player2_choice]);

  if (!reveal) return null;

  const p1Win = reveal.result === 'player1_wins';
  const p2Win = reveal.result === 'player2_wins';
  const isDraw = reveal.result === 'draw';

  const winnerChoice = p1Win ? reveal.player1_choice : p2Win ? reveal.player2_choice : null;
  const loserChoice = p1Win ? reveal.player2_choice : p2Win ? reveal.player1_choice : null;
  const battleText = winnerChoice && loserChoice ? getBattleEffect(winnerChoice, loserChoice) : null;

  return (
    <div className={`${styles.revealRow} ${visible ? styles.revealRowVisible : ''}`}>
      {/* Player 1 */}
      <div className={`${styles.revealPlayer} ${p1Win ? styles.revealWinner : p2Win ? styles.revealLoser : styles.revealDraw}`}>
        <span className={`${styles.revealEmoji} ${p1Win ? styles.revealEmojiWin : p2Win ? styles.revealEmojiLose : styles.revealEmojiDraw}`}>
          {CHOICE_EMOJI[reveal.player1_choice]}
        </span>
        <span className={styles.revealName}>
          {match.player1.display_name ?? 'Player 1'}
        </span>
        {p1Win && <span className={styles.revealResultBadge} data-result="win">THẮNG 🏆</span>}
        {p2Win && <span className={styles.revealResultBadge} data-result="lose">THUA</span>}
        {isDraw && <span className={styles.revealResultBadge} data-result="draw">HÒA</span>}
      </div>

      {/* Center battle effect */}
      <div className={styles.revealCenter}>
        {battleText ? (
          <span className={styles.battleText}>{battleText}</span>
        ) : (
          <span className={styles.revealVs}>{isDraw ? '🤝' : '⚡'}</span>
        )}
      </div>

      {/* Player 2 */}
      <div className={`${styles.revealPlayer} ${p2Win ? styles.revealWinner : p1Win ? styles.revealLoser : styles.revealDraw}`}>
        <span className={`${styles.revealEmoji} ${p2Win ? styles.revealEmojiWin : p1Win ? styles.revealEmojiLose : styles.revealEmojiDraw}`}>
          {CHOICE_EMOJI[reveal.player2_choice]}
        </span>
        <span className={styles.revealName}>
          {match.player2?.display_name ?? 'Player 2'}
        </span>
        {p2Win && <span className={styles.revealResultBadge} data-result="win">THẮNG 🏆</span>}
        {p1Win && <span className={styles.revealResultBadge} data-result="lose">THUA</span>}
        {isDraw && <span className={styles.revealResultBadge} data-result="draw">HÒA</span>}
      </div>

      {reveal.is_random_tiebreak && (
        <div className={styles.tiebreakNote}>* Quyết định ngẫu nhiên</div>
      )}
    </div>
  );
}

function SpotlightMatch({
  match,
  myParticipantId,
  isMyMatch,
  subRoundStartedAt,
  subRoundTimerSec,
  myChoiceSubmitted,
  onChoose,
}: {
  match: RPSMatchState;
  myParticipantId: string | null;
  isMyMatch: boolean;
  subRoundStartedAt: number | null;
  subRoundTimerSec: number;
  myChoiceSubmitted: boolean;
  onChoose: (choice: 'rock' | 'paper' | 'scissors') => void;
}) {
  const remainMs = useTimerMs(
    match.status === 'in_progress' ? subRoundStartedAt : null,
    subRoundTimerSec,
  );
  const remainSec = remainMs !== null ? Math.ceil(remainMs / 1000) : null;
  const timerPct = remainMs !== null ? remainMs / (subRoundTimerSec * 1000) : 1;

  const isCompleted = match.status === 'completed';
  const hasReveal = !!match.last_reveal;
  const canChoose = isMyMatch && !myChoiceSubmitted && match.status === 'in_progress' && !isCompleted;

  return (
    <div className={styles.spotlight}>
      {/* Header */}
      <div className={styles.spotlightHeader}>
        <div className={styles.spotlightTitle}>
          {isMyMatch ? 'Trận của bạn' : 'Spotlight'}
          {isCompleted && <span className={styles.completedBadge}>Kết thúc</span>}
        </div>
        {!isCompleted && remainSec !== null && (
          <div className={styles.timerWrap}>
            <div
              className={styles.timerBar}
              style={{ width: `${Math.round(timerPct * 100)}%`, background: timerPct < 0.3 ? 'var(--color-danger)' : 'var(--color-success)' }}
            />
            <span className={styles.timerText}>{remainSec}s</span>
          </div>
        )}
      </div>

      {/* Players vs */}
      <div className={styles.spotlightVs}>
        <div className={`${styles.vsPlayer} ${match.winner_id === match.player1.participant_id ? styles.vsWinner : ''}`}>
          <Avatar src={match.player1.avatar_url ?? undefined} name={match.player1.display_name ?? '?'} size="xl" />
          <div className={styles.vsName}>{match.player1.display_name ?? 'Player 1'}</div>
          <div className={styles.vsScore}>{match.player1_games_won}</div>
          {match.ready_player_ids.includes(match.player1.participant_id) && match.status === 'in_progress' && !hasReveal && (
            <div className={styles.readyDot} title="Đã chọn" />
          )}
        </div>

        <div className={styles.vsSep}>
          {isCompleted && match.winner_id ? (
            <div className={styles.vsCompleted}>
              <Trophy size={24} color="var(--color-warning)" />
              <span className={styles.vsCompletedLabel}>Kết thúc</span>
            </div>
          ) : (
            <span className={styles.vsText}>VS</span>
          )}
          <div className={styles.vsFormat}>{match.match_format.replace('_', ' ')}</div>
          <div className={styles.vsSubRound}>Game {match.current_sub_round}</div>
        </div>

        <div className={`${styles.vsPlayer} ${match.winner_id === match.player2?.participant_id ? styles.vsWinner : ''}`}>
          {match.player2 ? (
            <>
              <Avatar src={match.player2.avatar_url ?? undefined} name={match.player2.display_name ?? '?'} size="xl" />
              <div className={styles.vsName}>{match.player2.display_name ?? 'Player 2'}</div>
              <div className={styles.vsScore}>{match.player2_games_won}</div>
              {match.ready_player_ids.includes(match.player2.participant_id) && match.status === 'in_progress' && !hasReveal && (
                <div className={styles.readyDot} title="Đã chọn" />
              )}
            </>
          ) : (
            <div className={styles.vsName}>Bye</div>
          )}
        </div>
      </div>

      {/* Reveal */}
      {hasReveal && <RevealDisplay match={match} myParticipantId={myParticipantId} />}

      {/* Choice buttons */}
      {canChoose && (
        <div className={styles.choiceRow}>
          <div className={styles.choiceHint}>Chọn của bạn:</div>
          <div className={styles.choiceBtns}>
            {(['rock', 'paper', 'scissors'] as const).map((c) => (
              <ChoiceButton
                key={c}
                choice={c}
                onClick={() => onChoose(c)}
                disabled={false}
                selected={false}
              />
            ))}
          </div>
        </div>
      )}

      {isMyMatch && myChoiceSubmitted && !hasReveal && match.status === 'in_progress' && (
        <div className={styles.waitingReveal}>✅ Đã chọn — chờ đối thủ...</div>
      )}
    </div>
  );
}

function MatchCard({
  match,
  isSpotlit,
  onClick,
}: {
  match: RPSMatchState;
  isSpotlit: boolean;
  onClick: () => void;
}) {
  return (
    <button
      className={`${styles.matchCard} ${isSpotlit ? styles.matchCardActive : ''} ${match.status === 'completed' ? styles.matchCardDone : ''}`}
      onClick={onClick}
    >
      <div className={styles.cardPlayer}>
        <Avatar src={match.player1.avatar_url ?? undefined} name={match.player1.display_name ?? '?'} size="sm" />
        <span className={styles.cardName}>{match.player1.display_name ?? 'P1'}</span>
        <span className={`${styles.cardScore} ${match.winner_id === match.player1.participant_id ? styles.cardWin : ''}`}>
          {match.player1_games_won}
        </span>
      </div>
      <span className={styles.cardVs}>VS</span>
      <div className={styles.cardPlayer}>
        {match.player2 ? (
          <>
            <Avatar src={match.player2.avatar_url ?? undefined} name={match.player2.display_name ?? '?'} size="sm" />
            <span className={styles.cardName}>{match.player2.display_name ?? 'P2'}</span>
            <span className={`${styles.cardScore} ${match.winner_id === match.player2.participant_id ? styles.cardWin : ''}`}>
              {match.player2_games_won}
            </span>
          </>
        ) : (
          <span className={styles.cardName}>Bye</span>
        )}
      </div>
    </button>
  );
}

function TournamentEndScreen({
  championId,
  finalStandings,
  participants,
  onLeave,
}: {
  championId: string;
  finalStandings: Array<{ participant_id: string; display_name: string | null; avatar_url: string | null; eliminated_in_round: number | null }>;
  participants: typeof gameStore extends (...args: any[]) => infer R ? R : never;
  onLeave: () => void;
}) {
  const champion = finalStandings.find((s) => s.participant_id === championId);
  return (
    <>
      <Confetti />
      <div className={styles.endScreen}>
        <div className={styles.endHeader}>
          <div style={{ animation: 'trophy-bounce 0.6s ease-in-out infinite alternate' }}>
            <Trophy size={64} color="var(--color-warning)" />
          </div>
          <h2 className={styles.endTitle}>🎉 Kết thúc Tournament!</h2>
          <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', marginTop: -8 }}>Chúc mừng người vô địch!</p>
        </div>
        {champion && (
          <div className={styles.championBox} style={{ animation: 'champion-glow 1.5s ease-in-out infinite alternate' }}>
            <div style={{ fontSize: 40, marginBottom: 4 }}>👑</div>
            <Avatar src={champion.avatar_url ?? undefined} name={champion.display_name ?? '?'} size="xl" />
            <div className={styles.championName}>{champion.display_name ?? 'Champion'}</div>
            <div className={styles.championLabel}>🏆 Vô địch</div>
          </div>
        )}
        <div className={styles.endStandings}>
          {finalStandings.map((s, i) => (
            <div key={s.participant_id} className={styles.endStandingRow}>
              <span className={styles.endRank}>#{i + 1}</span>
              <Avatar src={s.avatar_url ?? undefined} name={s.display_name ?? '?'} size="sm" />
              <span className={styles.endName}>{s.display_name ?? 'Player'}</span>
              {s.eliminated_in_round !== null && (
                <span className={styles.endElim}>Loại vòng {s.eliminated_in_round}</span>
              )}
            </div>
          ))}
        </div>
        <button className={styles.endLeaveBtn} onClick={onLeave}>
          Về Lobby
        </button>
      </div>
    </>
  );
}

// ---- Match Result Overlay (brief win/lose flash) ----

function MatchResultOverlay({ result, onDone }: { result: 'win' | 'lose'; onDone: () => void }) {
  const [visible, setVisible] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Fade in
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    const done = setTimeout(onDone, 2600);
    return () => { clearTimeout(t); clearTimeout(done); };
  }, [onDone]);

  // Mini confetti on win
  useEffect(() => {
    if (result !== 'win') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#facc15', '#4ade80', '#38bdf8', '#f43f5e', '#a78bfa', '#fb923c'];
    const pieces = Array.from({ length: 80 }, () => ({
      x: Math.random() * canvas.width,
      y: -10 - Math.random() * 80,
      vx: (Math.random() - 0.5) * 5,
      vy: 3 + Math.random() * 5,
      color: colors[Math.floor(Math.random() * colors.length)],
      w: 7 + Math.random() * 7,
      h: 4 + Math.random() * 4,
      rot: Math.random() * Math.PI * 2,
      rotV: (Math.random() - 0.5) * 0.18,
    }));

    let id: number;
    const tick = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (const p of pieces) {
        p.x += p.vx; p.y += p.vy; p.vy += 0.1; p.rot += p.rotV;
        ctx.save();
        ctx.globalAlpha = 0.9;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (pieces.some((p) => p.y < canvas.height + 20)) id = requestAnimationFrame(tick);
    };
    id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [result]);

  return (
    <div className={`${styles.matchOverlay} ${visible ? styles.matchOverlayVisible : ''} ${result === 'win' ? styles.matchOverlayWin : styles.matchOverlayLose}`}>
      {result === 'win' && (
        <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
      )}
      <div className={styles.matchOverlayContent}>
        {result === 'win' ? (
          <>
            <div className={styles.matchOverlayEmoji}>🏆</div>
            <div className={styles.matchOverlayTitle}>Bạn thắng!</div>
            <div className={styles.matchOverlaySub}>Tiếp tục vào vòng sau...</div>
          </>
        ) : (
          <>
            <div className={styles.matchOverlayEmoji}>💔</div>
            <div className={styles.matchOverlayTitle}>Bị loại</div>
            <div className={styles.matchOverlaySub}>Cảm ơn bạn đã tham gia!</div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Draw Animation Screen ----

function DrawingScreen({ roundLabel, participantCount }: { roundLabel: string; participantCount: number }) {
  const [dotCount, setDotCount] = useState(1);

  useEffect(() => {
    const id = setInterval(() => setDotCount((d) => (d % 3) + 1), 400);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={styles.drawingRoot}>
      <div className={styles.drawingBox}>
        <div className={styles.drawingTitle}>🎲 {roundLabel}</div>
        <div className={styles.drawingSubtitle}>
          Đang bốc thăm {participantCount} người chơi{'.'.repeat(dotCount)}
        </div>
        <div className={styles.drawingCards}>
          {Array.from({ length: Math.min(participantCount, 6) }).map((_, i) => (
            <div key={i} className={styles.drawingCard} style={{ animationDelay: `${i * 0.15}s` }}>
              <span className={styles.drawingCardQ}>?</span>
            </div>
          ))}
        </div>
        <div className={styles.drawingSpinner} />
      </div>
    </div>
  );
}

// ---- Bracket Ready Screen (user view) ----

function BracketReadyScreen({
  matches,
  roundLabel,
  tournamentRound,
  totalRounds,
  myParticipantId,
  bracketHistory,
}: {
  matches: RPSMatchState[];
  roundLabel: string;
  tournamentRound: number;
  totalRounds: number;
  myParticipantId: string | null;
  bracketHistory: Record<number, RPSMatchState[]>;
}) {
  return (
    <div className={styles.bracketReadyRoot}>
      <div className={styles.bracketReadyHeader}>
        <Trophy size={28} color="var(--color-warning)" />
        <h2 className={styles.bracketReadyTitle}>{roundLabel}</h2>
        <p className={styles.bracketReadyWait}>Chờ admin bắt đầu thi đấu...</p>
      </div>
      <BracketTree
        currentMatches={matches}
        currentRound={tournamentRound}
        totalRounds={totalRounds}
        myParticipantId={myParticipantId}
        bracketHistory={bracketHistory}
      />
    </div>
  );
}

// ---- Round Transition Screen (user view — chờ admin bắt đầu vòng tiếp) ----

function RoundTransitionScreen({
  tournamentRound,
  advancing,
  eliminated,
  myParticipantId,
}: {
  tournamentRound: number;
  advancing: string[];
  eliminated: string[];
  myParticipantId: string | null;
}) {
  const isAdvancing = myParticipantId ? advancing.includes(myParticipantId) : null;

  return (
    <div className={styles.transitionFullScreen}>
      <div className={styles.transitionBox}>
        <div className={styles.transitionTitle}>Vòng {tournamentRound} kết thúc!</div>
        <div className={styles.transitionStats}>
          <span className={styles.transitionAdvCount}>✅ {advancing.length} người tiếp tục</span>
          <span className={styles.transitionElimCount}>❌ {eliminated.length} người bị loại</span>
        </div>
        {isAdvancing === true && (
          <div className={styles.transitionMyResult + ' ' + styles.transitionMyResultWin}>
            🎉 Bạn tiếp tục vào vòng tiếp theo!
          </div>
        )}
        {isAdvancing === false && (
          <div className={styles.transitionMyResult + ' ' + styles.transitionMyResultLose}>
            Bạn đã bị loại ở vòng {tournamentRound}. Cảm ơn đã tham gia!
          </div>
        )}
        <div className={styles.transitionWaiting}>Chờ admin bắt đầu vòng tiếp theo...</div>
      </div>
    </div>
  );
}

// ---- Main Page ----

export default function TournamentPage() {
  const { roundId } = useParams<{ roundId: string }>();
  const navigate = useNavigate();
  if (!roundId) throw new Error('Missing roundId');

  useGameSocket(roundId, 'player');
  useRpsSocket(roundId);

  const myParticipantId = gameStore((s) => s.myParticipantId);
  const connectionStatus = gameStore((s) => s.connectionStatus);

  const phase = rpsStore((s) => s.phase);
  const tournament_round = rpsStore((s) => s.tournament_round);
  const participant_count = rpsStore((s) => s.participant_count);
  const initial_participant_count = rpsStore((s) => s.initial_participant_count);
  const bracketHistory = rpsStore((s) => s.bracketHistory);
  const matches = rpsStore((s) => s.matches);
  const spotlight_match_id = rpsStore((s) => s.spotlight_match_id);
  const my_match_id = rpsStore((s) => s.my_match_id);
  const my_choice_submitted = rpsStore((s) => s.my_choice_submitted);
  const sub_round_started_at = rpsStore((s) => s.sub_round_started_at);
  const sub_round_timer_sec = rpsStore((s) => s.sub_round_timer_sec);
  const advancing_ids = rpsStore((s) => s.advancing_ids);
  const eliminated_ids = rpsStore((s) => s.eliminated_ids);
  const next_round_starts_in_sec = rpsStore((s) => s.next_round_starts_in_sec);
  const champion_id = rpsStore((s) => s.champion_id);
  const final_standings = rpsStore((s) => s.final_standings);

  const chooseAction = useRpsChoose(roundId);
  const spotlightAction = useSpotlightMatch(roundId);

  const [muted, setMuted] = useState(false);
  const [matchOverlay, setMatchOverlay] = useState<'win' | 'lose' | null>(null);
  const overlayShownRef = useRef<string | null>(null); // track match_id đã show overlay

  // Detect khi MY match vừa completed → hiển thị overlay
  useEffect(() => {
    if (!my_match_id || !myParticipantId) return;
    const myMatch = matches.find((m) => m.match_id === my_match_id);
    if (!myMatch || myMatch.status !== 'completed') return;
    if (overlayShownRef.current === my_match_id) return; // đã show rồi
    overlayShownRef.current = my_match_id;
    const result = myMatch.winner_id === myParticipantId ? 'win' : 'lose';
    setMatchOverlay(result);
  }, [matches, my_match_id, myParticipantId]);

  useAudio('/audio/gameplay.mp3', {
    enabled: phase === 'round_active' || phase === 'bracket_ready',
    volume: muted ? 0 : 0.3,
    fadeOutMs: 1200,
  });

  const round_label = rpsStore((s) => s.round_label);
  // Dùng initial_participant_count để totalRounds không co lại khi vào vòng sau
  const effectiveCount = Math.max(initial_participant_count, participant_count);
  const totalRounds = effectiveCount > 1 ? Math.ceil(Math.log2(effectiveCount)) : 1;

  const spotlightMatch = matches.find((m) => m.match_id === spotlight_match_id) ?? matches[0] ?? null;
  const isMyMatch = !!my_match_id && spotlightMatch?.match_id === my_match_id;

  const handleChoose = useCallback(
    (choice: 'rock' | 'paper' | 'scissors') => {
      if (!spotlightMatch || !my_match_id) return;
      chooseAction(my_match_id, spotlightMatch.current_sub_round, choice);
    },
    [chooseAction, spotlightMatch, my_match_id],
  );

  const handleSpotlight = useCallback(
    (matchId: string) => {
      spotlightAction(matchId);
    },
    [spotlightAction],
  );

  const handleLeave = () => {
    rpsStore.getState().reset();
    gameStore.getState().reset();
    navigate('/lobby', { replace: true });
  };

  if (phase === 'drawing') {
    return (
      <div className={styles.root}>
        <div className={styles.fullLayout}>
          <div className={styles.fullMain}>
            <DrawingScreen roundLabel={round_label} participantCount={participant_count} />
          </div>
          <div className={styles.fullChat}>
            <ChatPanel roundId={roundId} theme="dark" />
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'bracket_ready') {
    return (
      <div className={styles.root}>
        <div className={styles.fullLayout}>
          <div className={styles.fullMain}>
            <BracketReadyScreen
              matches={matches}
              roundLabel={round_label}
              tournamentRound={tournament_round}
              totalRounds={totalRounds}
              myParticipantId={myParticipantId}
              bracketHistory={bracketHistory}
            />
          </div>
          <div className={styles.fullChat}>
            <ChatPanel roundId={roundId} theme="dark" />
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'round_transitioning') {
    return (
      <div className={styles.root}>
        <div className={styles.fullLayout}>
          <div className={styles.fullMain}>
            <RoundTransitionScreen
              tournamentRound={tournament_round}
              advancing={advancing_ids}
              eliminated={eliminated_ids}
              myParticipantId={myParticipantId}
            />
          </div>
          <div className={styles.fullChat}>
            <ChatPanel roundId={roundId} theme="dark" />
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'tournament_ended' && champion_id) {
    return (
      <div className={styles.root}>
        <div className={styles.fullLayout}>
          <div className={styles.fullMain}>
            <TournamentEndScreen
              championId={champion_id}
              finalStandings={final_standings}
              participants={null as any}
              onLeave={handleLeave}
            />
          </div>
          <div className={styles.fullChat}>
            <ChatPanel roundId={roundId} theme="dark" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {matchOverlay && (
        <MatchResultOverlay result={matchOverlay} onDone={() => setMatchOverlay(null)} />
      )}
      {connectionStatus === 'reconnecting' && (
        <div className={styles.connBanner}>Đang kết nối lại...</div>
      )}

      {/* Top bar */}
      <div className={styles.topBar}>
        <div className={styles.topLeft}>
          <Swords size={18} />
          <span className={styles.topTitle}>Kéo Búa Bao — Vòng {tournament_round}</span>
        </div>
        <div className={styles.topRight}>
          <span className={styles.topMatchCount}>{matches.filter((m) => m.status !== 'completed').length} trận đang chơi</span>
          <button
            className={styles.muteBtn}
            onClick={() => setMuted((m) => !m)}
            title={muted ? 'Bật nhạc' : 'Tắt nhạc'}
          >
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
        </div>
      </div>

      <div className={styles.layout}>
        {/* Left: match list */}
        <div className={styles.matchList}>
          <div className={styles.matchListTitle}>Danh sách trận</div>
          {matches.map((m) => (
            <MatchCard
              key={m.match_id}
              match={m}
              isSpotlit={m.match_id === spotlight_match_id}
              onClick={() => handleSpotlight(m.match_id)}
            />
          ))}
          {my_match_id && (
            <button
              className={styles.myMatchBtn}
              onClick={() => handleSpotlight(my_match_id)}
            >
              👤 Xem trận của tôi
            </button>
          )}
        </div>

        {/* Center: spotlight */}
        <div className={styles.center}>
          {spotlightMatch ? (
            <SpotlightMatch
              match={spotlightMatch}
              myParticipantId={myParticipantId}
              isMyMatch={isMyMatch}
              subRoundStartedAt={sub_round_started_at}
              subRoundTimerSec={sub_round_timer_sec}
              myChoiceSubmitted={my_choice_submitted}
              onChoose={handleChoose}
            />
          ) : (
            <div className={styles.noMatch}>Đang chờ tournament bắt đầu...</div>
          )}
        </div>

        {/* Right: chat */}
        <div className={styles.right}>
          <ChatPanel roundId={roundId} theme="dark" />
        </div>
      </div>
    </div>
  );
}
